import { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { Check } from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { pricingTiers, professionalServices, type PricingTier } from '../data/landing-data';
import { useBookDemoStore } from '../store/book-demo-store';
import { PricingComparisonTable } from './pricing-comparison-table';

// ── 3D pricing card ─────────────────────────────────────────────

function PricingCard({ tier, index }: { tier: PricingTier; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const openDemo = useBookDemoStore((s) => s.open);

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  const rotateY = useSpring(useTransform(rawX, [-0.5, 0.5], [-8, 8]), {
    stiffness: 100,
    damping: 20,
  });
  const rotateX = useSpring(useTransform(rawY, [-0.5, 0.5], [6, -6]), {
    stiffness: 100,
    damping: 20,
  });
  const glareX = useTransform(rawX, [-0.5, 0.5], ['0%', '100%']);
  const glareY = useTransform(rawY, [-0.5, 0.5], ['0%', '100%']);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    rawX.set((e.clientX - rect.left) / rect.width - 0.5);
    rawY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ perspective: '900px' }}
    >
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        className={`group relative rounded-xl border p-6 lg:p-8 flex flex-col h-full cursor-default ${
          tier.highlighted
            ? 'border-blue-300 dark:border-blue-500/40 bg-gradient-to-b from-blue-50 to-violet-50/30 dark:from-blue-950/40 dark:to-violet-950/20 shadow-lg hover:shadow-xl dark:shadow-blue-500/5'
            : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 dark:backdrop-blur-sm shadow-sm hover:shadow-lg dark:hover:shadow-gray-900/30 hover:border-blue-200 dark:hover:border-blue-500/30'
        } transition-shadow duration-300`}
      >
        {/* Glare */}
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: useTransform(
              [glareX, glareY],
              ([x, y]) =>
                `radial-gradient(circle at ${x} ${y}, rgba(255,255,255,0.45) 0%, transparent 65%)`
            ),
          }}
        />

        {/* Badge */}
        {tier.badge && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md">
              {tier.badge}
            </span>
          </div>
        )}

        <div style={{ transform: 'translateZ(12px)' }} className="flex flex-col flex-1">
          {/* Name */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{tier.name}</h3>

          {/* Tag instead of price */}
          <div className="mb-4">
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20">
              Plan a medida · sin tarjeta
            </span>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">{tier.description}</p>

          {/* Features */}
          <ul className="space-y-3 mb-8 flex-1">
            {tier.features.map((feature) => (
              <li key={feature} className="flex items-start gap-2.5">
                <Check
                  className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    tier.highlighted
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-300 dark:text-gray-600'
                  }`}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">{feature}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button
            onClick={() => openDemo({ plan: tier.name })}
            className={`block w-full text-center px-6 py-3 rounded-lg font-medium text-sm transition-all ${
              tier.highlighted
                ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-500 hover:to-violet-500 shadow-lg shadow-blue-600/25 hover:shadow-[0_0_30px_rgba(99,102,241,0.3)] dark:hover:shadow-[0_0_30px_rgba(99,102,241,0.4)]'
                : 'border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {tier.cta}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Section ─────────────────────────────────────────────────────

export function PricingSection() {
  return (
    <section id="pricing" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <ScrollReveal className="text-center mb-12">
          <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 tracking-wider uppercase mb-3">
            Pricing
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Un plan para cada{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-cyan-600">
              etapa de tu negocio
            </span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Pricing ajustado al tamaño de tu equipo. Pide una demo y armamos un plan contigo.
          </p>
        </ScrollReveal>

        {/* Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {pricingTiers.map((tier, index) => (
            <PricingCard key={tier.name} tier={tier} index={index} />
          ))}
        </div>

        {/* Comparativa completa expandible */}
        <PricingComparisonTable />

        {/* Servicios Profesionales */}
        <ScrollReveal className="mt-20">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-center text-gray-900 dark:text-white mb-3">
              Servicios Profesionales
            </h3>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-10 text-sm">
              Setup, capacitación e integración disponibles como add-ons. Los detallamos en la demo.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {professionalServices.map((service) => (
                <div
                  key={service.name}
                  className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5 hover:border-blue-200 dark:hover:border-blue-500/30 transition-colors"
                >
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    {service.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
